import { useState, useEffect, useRef, useMemo } from "react";
import AgGrid from "../../../common/AgGrid";
import Icon from "../../../common/Icon";
import { GetListManagers, PostageMasterApi } from "../../../../services/accountService";
import * as XLSX from "xlsx";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate } from "react-router-dom";
import SuccessMessage from '../../../common/SuccessMessage';
import { canViewFieldOnScreen, hasPermission, isListAgent } from "../../../../utils/PermissionUtils";
// ===========================================================================
// SHARED FORMATTERS
// ===========================================================================
const dateFmt = (p) => {
  if (!p.value) return "";
  const d = new Date(p.value);
  if (isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
};


const numberFmt = (p) =>
  p.value != null && p.value !== ""
    ? Number(p.value).toLocaleString("en-US")
    : "";

const currencyFmt = (p) =>
  p.value != null && p.value !== ""
    ? "$" + Number(p.value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    : "";

// ------------------------------------------------------------------
// ✅ Centralized cell formatter — one place so Excel and PDF render
// identically (mirrors JobsModule / all other report tabs). These three
// grids have widely varying, ad-hoc field names, so formatting is
// inferred generically from the field name itself (same heuristic the
// original exportToExcelCommon already used) rather than a fixed list.
// ------------------------------------------------------------------
const formatValueForFieldGeneric = (field, value) => {
  if (value === null || value === undefined || value === "") return "";
  const f = String(field).toLowerCase();
  if (f.includes("date")) return dateFmt({ value });
  if (f.includes("qty") || f.includes("quantity") || f.includes("donor")) return numberFmt({ value });
  if (f.includes("cost") || f.includes("income") || f.includes("amount")) return currencyFmt({ value });
  if (f.includes("rate")) return value != null ? `${Number(value).toFixed(2)}%` : "";
  return String(value);
};

// Fields that look numeric/currency by name → right-aligned in PDF.
const isRightAlignField = (field) => {
  const f = String(field).toLowerCase();
  return (
    f.includes("qty") || f.includes("quantity") || f.includes("donor") ||
    f.includes("cost") || f.includes("income") || f.includes("amount") ||
    f.includes("rate") || f.includes("roi") || f.includes("times")
  );
};

// ------------------------------------------------------------------
// ✅ RBAC: field names hidden from List Agent on the Header and Detail
// reports. These must match the `field` keys used in
// getListAnalysisHeaderColumnDefs / getListAnalysisDetailColumnDefs
// below. LOL (getListOfListsColumnDefs) is NOT restricted for List
// Agent — it stays fully visible and fully exportable.
// ------------------------------------------------------------------
const LIST_AGENT_HIDDEN_HEADER_FIELDS = [
  "totalDonors",
  "totalIncome",
  "totalCost",
  "avgOrderQty",
  "avgMailedQty",
  "keptRate",
  "responseRate",
  "avgGift",
  "incomePerPiece",
  "productionCostPerPiece",
  "netPerPiece",
  "roi",
];

const LIST_AGENT_HIDDEN_DETAIL_FIELDS = [
  "donors",
  "nonDonors",
  "responseRate",
  "avgGift",
  "income",
  "cost",
  "netIncome",
  "incomePerPiece",
  "costPerPiece",
  "netPerPiece",
];

// ✅ Strips the role-restricted columns out of a column-def array. Used
// for both the on-screen grid and the Excel/PDF export paths so a
// hidden field can never leak through either route.
const filterColumnsForRole = (columnDefs, hiddenFields) => {
  if (!hiddenFields || hiddenFields.length === 0) return columnDefs;
  return columnDefs.filter((col) => !hiddenFields.includes(col.field));
};



const CustomTooltip = (props) => {
  const { value } = props;

  if (!value) return null;

  return (
    <div
      style={{
        backgroundColor: '#333',
        color: '#fff',
        padding: '8px 10px',
        borderRadius: '4px',
        fontSize: '12px',
        maxWidth: '300px',
        whiteSpace: 'normal',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }}
    >
      {value}
    </div>
  );
};

// ✅ ExportMenu now offers both Export to Excel and Print To Pdf, matching
// the icon set used across every other report/cost tab in the app.
const ExportMenu = ({
  show,
  setShow,
  exportRef,
  onExport,
  onPrintPDF,
}) => (
  <div style={{ position: "relative" }} ref={exportRef}>
    <button
      className="job-details-menu-button"
      onClick={() => setShow(prev => !prev)}
    >
      <span className="menu-dot"></span>
      <span className="menu-dot"></span>
      <span className="menu-dot"></span>
    </button>

    {show && (
      <div
        style={{
          position: "absolute",
          top: "100%",
          right: 0,
          marginTop: "8px",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          zIndex: 1000,
          minWidth: "200px",
          overflow: "hidden",
        }}
      >
        <div
          onClick={() => {
            onExport();
            setShow(false);
          }}
          style={{
            padding: "12px 16px",
            cursor: "pointer",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#333",
            borderBottom: "1px solid #f0f0f0",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="16" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          Export to Excel
        </div>

        {onPrintPDF && (
          <div
            onClick={() => {
              onPrintPDF();
              setShow(false);
            }}
            style={{
              padding: "12px 16px",
              cursor: "pointer",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#333",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print To PDF
          </div>
        )}
      </div>
    )}
  </div>
);

// ===========================================================================
// COLUMN DEFS
// ===========================================================================
const getListOfListsColumnDefs = (navigate, lolFilters, selectedClientIds, rowData) => [

  { headerName: "Client ID", field: "clientId", minWidth: 300, filter: "agTextColumnFilter" },
  {
    headerName: "Client", field: "client", minWidth: 300, filter: "agTextColumnFilter", tooltipField: 'client',
    tooltipComponent: 'customTooltip',
  },
  {
    headerName: "Job ID",
    field: "jobId",
    minWidth: 300,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => {
      const jobCode = params.value;

      return (
        <span
          style={{
            color: "#2E6F9F",
            textDecoration: "underline",
            cursor: "pointer",
            fontWeight: 500
          }}
          onClick={() =>
            navigate(`/job-details/${jobCode}`, {
              state: {
                fromReports: true,
                reportType: "List of Lists",
                reportFilters: { lolFilters: lolFilters || {} },
                selectedClientId: selectedClientIds || [],
                rowData: rowData || [],
              }
            })

          }

        >
          {jobCode}
        </span>
      );
    }
  },
  {
    headerName: "Job Name", field: "jobName", minWidth: 300, filter: "agTextColumnFilter",
    tooltipField: 'jobName',
    tooltipComponent: 'customTooltip',
  },
  { headerName: "Issue", field: "issue", minWidth: 300, filter: "agTextColumnFilter" },
  { headerName: "Technique", field: "technique", minWidth: 200, filter: "agTextColumnFilter" },
  { headerName: "Mail Code", field: "mailCode", minWidth: 200, filter: "agTextColumnFilter" },
  { headerName: "Test", field: "test", minWidth: 200, filter: "agTextColumnFilter" },
  { headerName: "Postage", field: "postage", minWidth: 300, filter: "agTextColumnFilter" },
  { headerName: "Mail Date", field: "mailDate", minWidth: 200,   comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB), filter: "agDateColumnFilter", valueFormatter: dateFmt },
  { headerName: "Quantity Mailed", field: "qtyMailed", minWidth: 200, filter: "agNumberColumnFilter", valueFormatter: numberFmt },
  { headerName: "Quantity Ordered", field: "qtyOrdered", minWidth: 200, filter: "agNumberColumnFilter", valueFormatter: numberFmt },
  { headerName: "Order Number", field: "orderNumber", minWidth: 200, filter: "agTextColumnFilter" },
  {
    headerName: "List Name", field: "listName", minWidth: 300, tooltipField: 'listName',
    tooltipComponent: 'customTooltip', filter: "agTextColumnFilter"
  },
  {
    headerName: "List Select", field: "listSelect", tooltipField: 'listSelect',
    tooltipComponent: 'customTooltip', minWidth: 300, filter: "agTextColumnFilter"
  },
  { headerName: "List ID", field: "listId", minWidth: 200, filter: "agTextColumnFilter" },
  { headerName: "List Cost Estimate", field: "listCostEstimate", minWidth: 250, filter: "agNumberColumnFilter", valueFormatter: currencyFmt },
  { headerName: "Postage In", field: "postageIn", minWidth: 200, filter: "agNumberColumnFilter", valueFormatter: currencyFmt },
  { headerName: "Postage Out", field: "postageOut", minWidth: 250, filter: "agNumberColumnFilter", valueFormatter: currencyFmt },
];

const tooltipCellStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

const getListAnalysisHeaderColumnDefs = () => [
  { headerName: "List ID", field: "listId", minWidth: 150, filter: "agTextColumnFilter" },
  {
    headerName: "List Name", field: "listName", minWidth: 300, filter: "agTextColumnFilter", tooltipField: 'listName',
    tooltipComponent: 'customTooltip', cellStyle: tooltipCellStyle
  },
  // { headerName: "List Select", field: "listSelect", minWidth: 360, filter: "agTextColumnFilter" },
  { headerName: "Times Used", field: "timesUsed", minWidth: 360, filter: "agNumberColumnFilter" },
  {
    headerName: "Last Used",
    field: "lastUsed",
    minWidth: 360,
    filter: "agTextColumnFilter",
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ""),
  },
  {
    headerName: "Order Quantity",
    field: "orderQty",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : ""),
  },
  {
    headerName: "Quantity Mailed",
    field: "qtyMailed",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : ""),
  },
  { headerName: "Total Donors", field: "totalDonors", minWidth: 240, filter: "agNumberColumnFilter", valueFormatter: numberFmt },
  {
    headerName: "Total Income",
    field: "totalIncome",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toLocaleString()}` : ""),
  },
  {
    headerName: "Total List Cost",
    field: "totalListCost",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toLocaleString()}` : ""),
  },
  {
    headerName: "Total Cost",
    field: "totalCost",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toLocaleString()}` : ""),
  },
  {
    headerName: "Avg Order Qty",
    field: "avgOrderQty",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : ""),
  },
  {
    headerName: "Avg Mailed Qty",
    field: "avgMailedQty",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : ""),
  },
  {
    headerName: "Kept Rate",
    field: "keptRate",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `${Number(p.value).toFixed(2)}%` : ""),
  },
  {
    headerName: "Response Rate",
    field: "responseRate",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `${Number(p.value).toFixed(2)}%` : ""),
  },
  {
    headerName: "Average Gift",
    field: "avgGift",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toLocaleString()}` : ""),
  },
  {
    headerName: "Income Per Piece",
    field: "incomePerPiece",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toFixed(2)}` : ""),
  },
  {
    headerName: "List Cost/M",
    field: "listCostPerM",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toFixed(2)}` : ""),
  },
  {
    headerName: "Production Cost/Piece",
    field: "productionCostPerPiece",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toFixed(2)}` : ""),
  },
  {
    headerName: "Net Per Piece",
    field: "netPerPiece",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toFixed(2)}` : ""),
  },
  {
    headerName: "ROI",
    field: "roi",
    minWidth: 360,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? Number(p.value).toFixed(2) : ""),
  },
  {
    headerName: "Returns Through",
    field: "returnsThrough",
    minWidth: 360,
    filter: "agTextColumnFilter",
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ""),
  },
  { headerName: "Job Type", field: "jobType", minWidth: 160, filter: "agTextColumnFilter" },
  { headerName: "R/E/H", field: "reh", minWidth: 200, filter: "agTextColumnFilter" },
];

const getListAnalysisDetailColumnDefs = (navigate, detailFilters, selectedClientIds, rowData) => [
  { headerName: "Client ID", field: "clientId", minWidth: 300, filter: "agTextColumnFilter" },
  {
    headerName: "Client Name", field: "clientName", minWidth: 300, filter: "agTextColumnFilter", tooltipField: 'clientName',
    tooltipComponent: 'customTooltip', cellStyle: tooltipCellStyle
  },
  {
    headerName: "Job ID",
    field: "jobId",
    minWidth: 300,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => {
      const jobCode = params.value;

      return (
        <span
          style={{
            color: "#2E6F9F",
            textDecoration: "underline",
            cursor: "pointer",
            fontWeight: 500
          }}
          onClick={() =>
            navigate(`/job-details/${jobCode}`, {
              state: {
                fromReports: true,
                reportType: "List Analysis - Detail",
                reportFilters: { detailFilters: detailFilters || {} },
                selectedClientId: selectedClientIds || [],
                rowData: rowData || [],
              }
            })
          }
        >
          {jobCode}
        </span>
      );
    }
  },
  {
    headerName: "Job Name", field: "jobName", minWidth: 380, filter: "agTextColumnFilter", tooltipField: 'jobName',
    tooltipComponent: 'customTooltip', cellStyle: tooltipCellStyle
  },
  { headerName: "Job Type", field: "jobType", minWidth: 300, filter: "agTextColumnFilter" },
  { headerName: "Technique", field: "technique", minWidth: 300, filter: "agTextColumnFilter" },
  {
    headerName: "Mail Date",
    field: "mailDate",
    minWidth: 300,
      comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
    filter: "agTextColumnFilter",
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ""),
  },
  { headerName: "Mail Code", field: "mailCode", minWidth: 300, filter: "agTextColumnFilter" },
  {
    headerName: "List Name", field: "listName", minWidth: 450, filter: "agTextColumnFilter", tooltipField: 'listName',
    tooltipComponent: 'customTooltip', cellStyle: tooltipCellStyle
  },
  {
    headerName: "List Select", field: "listSelect", minWidth: 450, filter: "agTextColumnFilter", tooltipField: 'listSelect',
    tooltipComponent: 'customTooltip', cellStyle: tooltipCellStyle
  },
  {
    headerName: "Quantity Mailed",
    field: "qtyMailed",
    minWidth: 300,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : ""),
  },
  {
    headerName: "Order Quantity",
    field: "orderQty",
    minWidth: 300,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : ""),
  },
  { headerName: "Donors", field: "donors", minWidth: 300, filter: "agNumberColumnFilter", valueFormatter: numberFmt },
  { headerName: "Non-Donors", field: "nonDonors", minWidth: 300, filter: "agNumberColumnFilter", valueFormatter: numberFmt },
  {
    headerName: "Response Rate",
    field: "responseRate",
    minWidth: 300,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `${Number(p.value).toFixed(2)}%` : ""),
  },
  {
    headerName: "Average Gift",
    field: "avgGift",
    minWidth: 300,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toLocaleString()}` : ""),
  },
  {
    headerName: "Income",
    field: "income",
    minWidth: 300,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toLocaleString()}` : ""),
  },
  {
    headerName: "Cost",
    field: "cost",
    minWidth: 300,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toLocaleString()}` : ""),
  },
  {
    headerName: "Net Income",
    field: "netIncome",
    minWidth: 300,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toLocaleString()}` : ""),
  },
  {
    headerName: "Income Per Piece",
    field: "incomePerPiece",
    minWidth: 300,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toFixed(2)}` : ""),
  },
  {
    headerName: "Cost Per Piece",
    field: "costPerPiece",
    minWidth: 300,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toFixed(2)}` : ""),
  },
  {
    headerName: "Net Per Piece",
    field: "netPerPiece",
    minWidth: 300,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toFixed(2)}` : ""),
  },
  {
    headerName: "Returns Through",
    field: "returnsThrough",
    minWidth: 300,
    filter: "agTextColumnFilter",
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ""),
  },
  {
    headerName: "Last Update",
    field: "lastUpdate",
      comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
    minWidth: 300,
    filter: "agTextColumnFilter",
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ""),
  },
  { headerName: "Last Updated By", field: "lastUpdatedBy", minWidth: 260, filter: "agTextColumnFilter" },
  {
    headerName: "Total List Cost",
    field: "totalListCost",
    minWidth: 300,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value != null ? `$${Number(p.value).toLocaleString()}` : ""),
  },
  { headerName: "Order Type", field: "orderType", minWidth: 220, filter: "agTextColumnFilter" },
  // { headerName: "R/E/H", field: "reh", minWidth: 180, filter: "agTextColumnFilter" },
];

// ===========================================================================
// FILTER HELPERS (client-side)
// ===========================================================================
const containsText = (value, query) =>
  String(value ?? "").toLowerCase().includes(String(query ?? "").toLowerCase());

const inDateRange = (value, from, to) => {
  if (!from && !to) return true; // no range set → keep all rows
  if (!value) return false; // range set but row has no date → drop
  const d = new Date(value);
  if (isNaN(d.getTime())) return false;
  if (from) {
    const start = new Date(from);
    start.setHours(0, 0, 0, 0); // include the whole "from" day
    if (d < start) return false;
  }
  if (to) {
    const end = new Date(to);
    end.setHours(23, 59, 59, 999); // include the whole "to" day
    if (d > end) return false;
  }
  return true;
};

const JobIdMultiSelect = ({ jobs, selectedIds, setSelectedIds }) => {
  const [jobSearch, setJobSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const list = jobs || [];

  const filteredJobs = useMemo(
    () =>
      list.filter((j) =>
        String(j.jobCode ?? "").toLowerCase().includes(jobSearch.toLowerCase())
      ),
    [list, jobSearch]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggle = (jobID) => {
    const updated = selectedIds.includes(jobID)
      ? selectedIds.filter((id) => id !== jobID)
      : [...selectedIds, jobID];
    setSelectedIds(updated);
  };

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      {/* INPUT BOX */}
      <div
        onClick={() => setOpen(true)}
        style={{
          minHeight: "38px",
          maxHeight: "100px",
          border: "1px solid #ccc",
          borderRadius: "6px",
          padding: "0px 8px",
          display: "flex",
          flexWrap: "wrap",
          gap: "4px",
          backgroundColor: "#fff",
          alignItems: "center",
          alignContent: "flex-start",
          cursor: "text",
          overflowY: "auto"
        }}
      >
        {/* CHIPS */}
        {selectedIds.length > 1 ? (
          <span style={{ background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", whiteSpace: "nowrap", fontSize: "13px" }}>
            {selectedIds.length} jobs selected
            <span
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIds([]);
              }}
              style={{ marginLeft: 6, cursor: "pointer" }}
            >
              ×
            </span>
          </span>
        ) : (
          list
            .filter((j) => selectedIds.includes(j.jobID))
            .map((j) => (
              <span
                key={j.jobID}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  background: "#e9ecef",
                  padding: "2px 6px",
                  borderRadius: "3px",
                  fontSize: "13px",
                  whiteSpace: "nowrap"
                }}
              >
                {j.jobCode}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedIds(selectedIds.filter((id) => id !== j.jobID));
                  }}
                  style={{ marginLeft: 6, cursor: "pointer" }}
                >
                  ×
                </span>
              </span>
            ))
        )}

        {/* SEARCH */}
        <input
          type="text"
          placeholder={selectedIds.length === 0 ? "Select jobs..." : ""}
          value={jobSearch}
          onChange={(e) => {
            setJobSearch(e.target.value);
            setOpen(true);
          }}
          style={{
            flex: 1,
            minWidth: selectedIds.length === 0 ? "120px" : "40px",
            width: "40px",
            border: "none",
            outline: "none",
            fontSize: "14px",
            padding: 0
          }}
        />
      </div>

      {/* DROPDOWN */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            borderRadius: "4px",
            marginTop: "4px",
            zIndex: 9999,
            maxHeight: "250px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            overflow: "hidden"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
            <button
              style={{
                background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F',
                padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
              }}
              onClick={() => {
                const allIds = filteredJobs.map((j) => j.jobID);
                const merged = [...new Set([...selectedIds, ...allIds])];
                setSelectedIds(merged);
              }}
            >
              Select All
            </button>
            <button
              style={{
                background: 'none', border: '1px solid #dc3545', color: '#dc3545',
                padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
              }}
              onClick={() => setSelectedIds([])}
            >
              Clear All
            </button>
          </div>

          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job) => (
                <div
                  key={job.jobID}
                  onClick={() => {
                    toggle(job.jobID);
                    setJobSearch("");
                  }}
                  style={{
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    backgroundColor: selectedIds.includes(job.jobID) ? "#e6f7ff" : "#fff"
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(job.jobID)}
                    readOnly
                    style={{ marginRight: "6px" }}
                  />
                  <span style={{ fontSize: "12px" }}>{job.jobCode}</span>
                </div>
              ))
            ) : (
              <div style={{ padding: "8px", color: "#888" }}>No jobs found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PostageMultiSelect = ({ postages, selectedIds, setSelectedIds }) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const list = postages || [];

  const filtered = useMemo(
    () =>
      list.filter((p) =>
        String(p.postageName ?? "").toLowerCase().includes(search.toLowerCase())
      ),
    [list, search]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggle = (postageID) => {
    const updated = selectedIds.includes(postageID)
      ? selectedIds.filter((id) => id !== postageID)
      : [...selectedIds, postageID];
    setSelectedIds(updated);
  };

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      {/* INPUT BOX */}
      <div
        onClick={() => setOpen(true)}
        style={{
          minHeight: "38px",
          maxHeight: "100px",
          border: "1px solid #ccc",
          borderRadius: "6px",
          padding: "0px 8px",
          display: "flex",
          flexWrap: "wrap",
          gap: "4px",
          backgroundColor: "#fff",
          alignItems: "center",
          alignContent: "flex-start",
          cursor: "text",
          overflowY: "auto"
        }}
      >
        {/* CHIPS */}
        {selectedIds.length > 1 ? (
          <span style={{ background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", whiteSpace: "nowrap", fontSize: "13px" }}>
            {selectedIds.length} postages selected
            <span
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIds([]);
              }}
              style={{ marginLeft: 6, cursor: "pointer" }}
            >
              ×
            </span>
          </span>
        ) : (
          list
            .filter((p) => selectedIds.includes(p.postageID))
            .map((p) => (
              <span
                key={p.postageID}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  background: "#e9ecef",
                  padding: "2px 6px",
                  borderRadius: "3px",
                  fontSize: "13px",
                  whiteSpace: "nowrap"
                }}
              >
                {p.postageName}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedIds(selectedIds.filter((id) => id !== p.postageID));
                  }}
                  style={{ marginLeft: 6, cursor: "pointer" }}
                >
                  ×
                </span>
              </span>
            ))
        )}

        {/* SEARCH */}
        <input
          type="text"
          placeholder={selectedIds.length === 0 ? "Select postage..." : ""}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          style={{
            flex: 1,
            minWidth: selectedIds.length === 0 ? "120px" : "40px",
            width: "40px",
            border: "none",
            outline: "none",
            fontSize: "14px",
            padding: 0
          }}
        />
      </div>

      {/* DROPDOWN */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            borderRadius: "4px",
            marginTop: "4px",
            zIndex: 9999,
            maxHeight: "250px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            overflow: "hidden"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
            <button
              style={{
                background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F',
                padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
              }}
              onClick={() => {
                const allIds = filtered.map((p) => p.postageID);
                const merged = [...new Set([...selectedIds, ...allIds])];
                setSelectedIds(merged);
              }}
            >
              Select All
            </button>
            <button
              style={{
                background: 'none', border: '1px solid #dc3545', color: '#dc3545',
                padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
              }}
              onClick={() => setSelectedIds([])}
            >
              Clear All
            </button>
          </div>

          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {filtered.length > 0 ? (
              filtered.map((p) => (
                <div
                  key={p.postageID}
                  onClick={() => {
                    toggle(p.postageID);
                    setSearch("");
                  }}
                  style={{
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    backgroundColor: selectedIds.includes(p.postageID) ? "#e6f7ff" : "#fff"
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(p.postageID)}
                    readOnly
                    style={{ marginRight: "6px" }}
                  />
                  <span style={{ fontSize: "12px" }}>{p.postageName}</span>
                </div>
              ))
            ) : (
              <div style={{ padding: "8px", color: "#888" }}>No postage found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// COMPONENT
// ===========================================================================
const ListAnalysisTab = ({
  styles,
  clients,
  jobTypes,
  listAnalysisType,
  setListAnalysisType,
  selectedClientId,
  handleClientChange,
  jobDetailsJobId,
  searchText,
  setSearchText,
  onGridReady,
  setListOfListsData,
  setListAnalysisHeaderData,
  setListAnalysisDetailData,
  // List of Lists
  listOfListsData,
  fetchListOfLists,
  fetchJobsByClient,
  lolFilters,
  setLolFilters,
  // Header
  listAnalysisHeaderData,
  headerFilters,
  setHeaderFilters,
  fetchListAnalysisHeader,
  // Detail
  listAnalysisDetailData,
  detailFilters,
  setDetailFilters,
  fetchListAnalysisDetail,
}) => {
  // ✅ ref to access AgGrid's exposed methods (getVisibleColumns, etc.)
  //    Mirrors the pattern used across every other cost/report tab so
  //    drag/drop column visibility is respected on export. Only one of
  //    the three grids below is ever mounted at a time (gated by
  //    listAnalysisType), so a single ref is sufficient.
  const agGridRef = useRef(null);

  // ✅ grid api ref — set in onGridReady (wrapped below so the parent's
  //    own onGridReady callback still fires). Used to read
  //    selected/filtered rows for export at export time, exactly like
  //    JobsModule/JobReportsTab's gridApiRef. Only one of the three grids
  //    is ever mounted at once, so a single ref is sufficient here too.
  const gridApiRef = useRef(null);

  const gridProps = {
    ref: agGridRef,
    enableSorting: true,
    enableFilter: true,
    pagination: true,
    height: "500px",
    quickFilterText: searchText,
    hideExportButton: true,
    hideSideBar: true,
    onQuickFilterChange: setSearchText,
    onGridReady: (params) => {
      gridApiRef.current = params.api;
      if (typeof onGridReady === 'function') onGridReady(params);
    },
    onRowClicked: (params) => params.data,
  };

  // ✅ RBAC — which List Analysis report types this user can see, and
  //    whether they can export at all. Both Client and Consultant have
  //    'lol', 'list-analysis-header', 'list-analysis-detail' hidden, and
  //    'trend-analysis' visible per their fieldPermissionDetails.
  const canViewLol = canViewFieldOnScreen('Reports', 'ReportsList', 'lol');
  const canViewHeader = canViewFieldOnScreen('Reports', 'ReportsList', 'list-analysis-header');
  const canViewDetail = canViewFieldOnScreen('Reports', 'ReportsList', 'list-analysis-detail');
  // const canViewTrend = canViewFieldOnScreen('Reports', 'ReportsList', 'trend-analysis');
  const canExportReports = hasPermission('Reports.Export');

  // ✅ RBAC: List Agent — LOL stays fully visible/exportable; Header and
  // Detail each hide a fixed set of financial/performance columns.
  const headerHiddenFields = isListAgent() ? LIST_AGENT_HIDDEN_HEADER_FIELDS : [];
  const detailHiddenFields = isListAgent() ? LIST_AGENT_HIDDEN_DETAIL_FIELDS : [];

  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);
  // state (near postages)
  const [listManagers, setListManagers] = useState([]);

  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const toggleFilters = () => setShowFilters(!showFilters);
  const [postages, setPostages] = useState([]);

  // Trend Analysis date state
  // const [trendFromDate, setTrendFromDate] = useState("");
  // const [trendToDate, setTrendToDate] = useState("");

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');


  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef(null);


  useEffect(() => {
    const handler = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ✅ RBAC — if the current listAnalysisType isn't one this user can view
  //    (e.g. the default "List of Lists" for a restricted user), fall back
  //    to the first report type they're actually allowed to see.
  useEffect(() => {
    const optionAllowed = {
      "List of Lists": canViewLol,
      "List Analysis - Header": canViewHeader,
      "List Analysis - Detail": canViewDetail,

    };
    if (!optionAllowed[listAnalysisType]) {
      const firstAllowed = Object.keys(optionAllowed).find((k) => optionAllowed[k]);
      if (firstAllowed) setListAnalysisType(firstAllowed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // when client changes → reset jobs
    setLolFilters((p) => ({ ...p, jobIds: [] }));
    setDetailFilters((p) => ({ ...p, jobIds: [] }));
  }, [selectedClientIds]);



  useEffect(() => {
    const loadPostages = async () => {
      try {
        const res = await PostageMasterApi();
        setPostages(Array.isArray(res) ? res : (res?.data || []));
      } catch (err) {
        console.error("Failed to load postage master", err);
        setPostages([]);
      }
    };
    loadPostages();
  }, []);

  // ✅ Restore client selection for "List of Lists" when returning from Job Details via Reports
  const lolRestoredRef = useRef(false);
  useEffect(() => {
    if (
      !lolRestoredRef.current &&
      listAnalysisType === "List of Lists" &&
      Array.isArray(selectedClientId) &&
      selectedClientId.length > 0 &&
      clients.length > 0 &&
      listOfListsData.length > 0
    ) {
      const matchedClients = clients.filter((c) => selectedClientId.includes(c.clientID));
      setSelectedClients(matchedClients);
      setSelectedClientIds(selectedClientId);
      fetchJobsByClient?.(selectedClientId);
      lolRestoredRef.current = true;
    }
  }, [selectedClientId, clients, listOfListsData, listAnalysisType, fetchJobsByClient]);

  // ✅ ADD THIS EFFECT - reset Job IDs when client selection changes
  useEffect(() => {
    setHeaderFilters((p) => ({ ...p, jobIds: [] }));
  }, [selectedClientIds]);

  // ------------------------------------------------------------------
  // ✅ Helper: get the list of column field/headerName pairs that should
  // appear in an export. Uses AgGrid's getVisibleColumns() so any column
  // the user dragged off the grid is automatically excluded. Falls back
  // to the full column list ONLY when the grid api genuinely isn't
  // available yet — trusts the array whenever the api actually responds,
  // even if it's empty (same fix applied everywhere else in the app).
  // ------------------------------------------------------------------
  const getExportColumnsFor = (columnDefs) => {
    const fallback = columnDefs
      .filter((col) => col.field)
      .map((col) => ({ field: col.field, headerName: col.headerName || col.field }));

    if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
      const visible = agGridRef.current.getVisibleColumns();
      if (Array.isArray(visible)) {
        return visible.filter((c) => c.field && c.field !== 'action');
      }
    }
    return fallback;
  };

  // ------------------------------------------------------------------
  // ✅ Helper: only real data rows from a given source (excludes the
  // totals placeholder, defensively, though none of these three reports
  // currently use one).
  // ------------------------------------------------------------------
  const getExportableRows = (source) => (source || []).filter((r) => !r?.isTotalsRow);

  // ------------------------------------------------------------------
  // ✅ getExportRows — mirrors JobsModule/JobReportsTab's full three-tier
  // scope rule (checked/"selected" rows take priority over the active
  // grid filter, which takes priority over "everything"), computed live
  // from the grid api at export time rather than from a snapshot state:
  //
  //   • Compute the rows AG Grid is showing after any column filter /
  //     quick-search (forEachNodeAfterFilter) — the "visible" set.
  //   • Of the checked rows (getSelectedRows), keep only those still
  //     visible -> "checkedVisible".
  //   1. If checkedVisible is non-empty -> export those.
  //   2. Else if a filter is active (visible !== all) -> export all
  //      visible, even if that's zero — never silently falls back to the
  //      full dataset.
  //   3. Else -> export everything (i.e. the already custom-filtered
  //      data passed in, such as filteredLolData).
  //
  // Returns { rows, scope } where scope is 'selected' | 'filtered' | 'all'.
  // ------------------------------------------------------------------
  const getExportRows = (baseData) => {
    const api = gridApiRef.current;
    const allRows = getExportableRows(baseData);

    let visible = null;
    if (api && typeof api.forEachNodeAfterFilter === 'function') {
      const rows = [];
      api.forEachNodeAfterFilter((node) => {
        if (node.data && !node.data.isTotalsRow) rows.push(node.data);
      });
      visible = rows;
    }

    const isFiltered = visible !== null && visible.length !== allRows.length;

    let selected = [];
    if (api && typeof api.getSelectedRows === 'function') {
      selected = api.getSelectedRows().filter((r) => r && !r.isTotalsRow);
    }

    if (selected.length > 0) {
      if (visible !== null) {
        const visibleSet = new Set(visible);
        const checkedVisible = selected.filter((r) => visibleSet.has(r));
        if (checkedVisible.length > 0) {
          return { rows: checkedVisible, scope: 'selected' };
        }
      } else {
        return { rows: selected, scope: 'selected' };
      }
    }

    if (isFiltered) {
      return { rows: visible, scope: 'filtered' };
    }

    if (allRows.length === 0) {
      return { rows: [], scope: 'all' };
    }
    return {
      rows: visible !== null ? visible : allRows,
      scope: 'all',
    };
  };

  const exportToExcelCommon = ({ data, columnDefs, sheetName }) => {
    const { rows: exportRows } = getExportRows(data);

    if (!exportRows || exportRows.length === 0) {
      setMessage('No data to export');
      setMessageType('error');  // ✅ ADD THIS LINE
      return;
    }

    try {
      const columnsToExport = getExportColumnsFor(columnDefs);
      if (columnsToExport.length === 0) {
        setMessage('No data to export');
        setMessageType('error');
        return;
      }

      const exportData = exportRows.map((row) => {
        const formattedRow = {};
        columnsToExport.forEach(({ field, headerName }) => {
          formattedRow[headerName] = formatValueForFieldGeneric(field, row[field]);
        });
        return formattedRow;
      });

      const ws = XLSX.utils.json_to_sheet(exportData);

      // auto width
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.min(
          Math.max(
            key.length,
            ...exportData.map(r => (r[key] ? String(r[key]).length : 0))
          ) + 2,
          40
        )
      }));

      ws["!cols"] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const date = new Date();
      const fileName = `${sheetName}_${date.toISOString().split("T")[0]}.xlsx`;

      XLSX.writeFile(wb, fileName);
      setMessage('Export completed successfully!');
      setMessageType('success');

    } catch (e) {
      console.error(e);
      setMessage('Error exporting data to Excel');
      setMessageType('error');
    }
  };

  // ------------------------------------------------------------------
  // ✅ PRINT TO PDF — shared across List of Lists / Header / Detail.
  // Same styled header card used everywhere else in the app; no KPI
  // stats bar or TOTAL footer row here since none of these three reports
  // have a pre-existing totals concept (unlike the cost tabs).
  // ------------------------------------------------------------------
  const printToPDFCommon = ({ data, columnDefs, sheetName, titleText, subtitleText }) => {
    const { rows: validData, scope } = getExportRows(data);

    if (!validData || validData.length === 0) {
      setMessage('No data to export');
      setMessageType('error');
      return;
    }

    try {
      const columnsToExport = getExportColumnsFor(columnDefs);
      if (columnsToExport.length === 0) {
        setMessage('No data to export');
        setMessageType('error');
        return;
      }

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const marginX = 20;
      const cardX = marginX;
      const cardY = 20;
      const cardWidth = pageWidth - marginX * 2;

      const currentDate = new Date();
      const reportDateStr = `${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;

      const headerBarHeight = 60;
      const detailsBarHeight = 22;
      const cardHeight = headerBarHeight + detailsBarHeight;

      const BLUE_DARK = [30, 75, 120];
      const BLUE_DEEP = [22, 58, 95];

      // ── 1) Header bar ──────────────────────────────────────────
      doc.setFillColor(...BLUE_DARK);
      doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');

      const logoW = 90;
      doc.setFillColor(...BLUE_DEEP);
      doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(180, 200, 220);
      doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, { align: 'center' });

      const titleAreaX = cardX + logoW;
      const titleAreaW = cardWidth - logoW;
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text(titleText, titleAreaX + titleAreaW / 2, cardY + headerBarHeight / 2 + 2, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(200, 215, 230);
      doc.text(subtitleText, titleAreaX + titleAreaW / 2, cardY + headerBarHeight / 2 + 16, { align: 'center' });

      const metaX = cardX + cardWidth - 14;
      const metaCenterY = cardY + headerBarHeight / 2;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(255, 255, 255);
      doc.text('Report', metaX, metaCenterY - 2, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(180, 200, 220);
      doc.text(reportDateStr, metaX, metaCenterY + 10, { align: 'right' });

      // ── 2) Details strip ──────────────────────────────────────
      const detailsY = cardY + headerBarHeight;
      doc.setFillColor(...BLUE_DEEP);
      doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text(`${sheetName.toUpperCase()} DETAILS`, cardX + 14, detailsY + detailsBarHeight / 2 + 3);

      const scopeLabel =
        scope === 'selected' ? 'Selected'
          : scope === 'filtered' ? 'Filtered'
            : 'All';
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(200, 215, 230);
      doc.text(
        `${scopeLabel} Records: ${validData.length}  ·  Confidential`,
        cardX + cardWidth - 12,
        detailsY + detailsBarHeight / 2 + 3,
        { align: 'right' }
      );

      // ── Table ────────────────────────────────────────────────
      const tableStartY = cardY + cardHeight + 10;
      const head = [columnsToExport.map((c) => c.headerName)];
      const body = validData.map((row) =>
        columnsToExport.map(({ field }) => formatValueForFieldGeneric(field, row[field]))
      );

      autoTable(doc, {
        head,
        body,
        startY: tableStartY,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 5, cellPadding: 3, overflow: 'linebreak', valign: 'middle' },
        headStyles: { fillColor: [46, 111, 159], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 5 },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        didParseCell: (data) => {
          if (data.section === 'body') {
            const col = columnsToExport[data.column.index];
            if (col && isRightAlignField(col.field)) {
              data.cell.styles.halign = 'right';
            }
          }
        },
        didDrawPage: () => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text(`Page ${pageCurrent} of ${pageCount}`, pageWidth - marginX, pageHeight - 10, { align: 'right' });
        },
      });

      const date = new Date();
      doc.save(`${sheetName.replace(/\s+/g, '-')}-${date.toISOString().split('T')[0]}.pdf`);

      setMessage('Export completed successfully!');
      setMessageType('success');
    } catch (e) {
      console.error(e);
      setMessage('Error exporting data to PDF');
      setMessageType('error');
    }
  };

  // ✅ CALLBACK TO CLOSE MESSAGE
  const handleMessageClose = () => {
    setMessage('');
    setMessageType('success');
  };


  // effect (next to loadPostages)
  useEffect(() => {
    const loadManagers = async () => {
      try {
        const res = await GetListManagers();
        setListManagers(Array.isArray(res) ? res : (res?.data || []));
      } catch (err) {
        console.error("Failed to load list managers", err);
        setListManagers([]);
      }
    };
    loadManagers();
  }, []);
  // Map jobID -> jobCode for client-side Job ID filtering

  const jobCodeById = useMemo(() => {
    const m = {};
    (jobDetailsJobId || []).forEach((j) => {
      m[j.jobID] = String(j.jobCode ?? "");
    });
    return m;
  }, [jobDetailsJobId]);

  // Map jobTypeID -> jobTypeName so we can keep the API filter on ID
  // while still doing client-side text filtering on the name.
  const jobTypeNameById = useMemo(() => {
    const m = {};
    (jobTypes || []).forEach((jt) => {
      m[String(jt.jobTypeID)] = String(jt.jobTypeName ?? "");
    });
    return m;
  }, [jobTypes]);

  const handleReportTypeChange = (e) => {
    const next = e.target.value;
    setListAnalysisType(next);

    // ✅ Clear all grid data
    setListOfListsData?.([]);
    setListAnalysisHeaderData?.([]);
    setListAnalysisDetailData?.([]);

    // ✅ Reset all filters
    setLolFilters?.({
      listName: "",
      fromDate: "",
      toDate: "",
      listId: "",
      jobIds: [],
      mailCode: "",
      postageIds: [],
    });

    setHeaderFilters?.({
      listName: "",
      manager: "",
      fromDate: "",
      toDate: "",
      jobType: "",
      search: "",
    });

    setDetailFilters?.({
      clientId: "",
      jobIds: [],
      fromDate: "",
      toDate: "",
      reh: "",
      jobType: "",
      mailCode: "",
      listName: "",
    });

    // ✅ Clear client selections
    setSelectedClients([]);
    setSelectedClientIds([]);
    setClientSearchTerm("");

    // ✅ Close dropdown
    setShowClientDropdown(false);

    // ✅ Reset search
    setSearchText?.("");

    // ✅ Trend fields reset

  };

  // -------- LIST OF LISTS client-side filtering --------
  const filteredLolData = useMemo(() => {
    const selectedJobCodes = (lolFilters.jobIds || []).map((id) => jobCodeById[id]).filter(Boolean);
    return listOfListsData.filter((row) => {
      if (lolFilters.listName && !containsText(row.listName, lolFilters.listName)) return false;
      if (lolFilters.listId && !containsText(row.listId, lolFilters.listId)) return false;
      if (selectedJobCodes.length > 0 && !selectedJobCodes.some((code) => containsText(row.jobId, code))) return false;
      if (lolFilters.mailCode && !containsText(row.mailCode, lolFilters.mailCode)) return false;
      if (!inDateRange(row.mailDate, lolFilters.fromDate, lolFilters.toDate)) return false;
      return true;
    });
  }, [listOfListsData, lolFilters, jobCodeById]);

  const handleClientChangeMulti = (client) => {
    const id = client.clientID;

    setSelectedClients(prev => {
      let updated;

      if (prev.some(c => c.clientID === id)) {
        updated = prev.filter(c => c.clientID !== id);
      } else {
        updated = [...prev, client];
      }

      const ids = updated.map(c => c.clientID);

      setSelectedClientIds(ids);
      fetchJobsByClient?.(ids);

      // ✅ IMPORTANT FIX — clear dependent job selections
      setLolFilters((p) => ({ ...p, jobIds: [] }));
      setDetailFilters((p) => ({ ...p, jobIds: [] }));

      return updated;
    });

    setClientSearchTerm("");
  };

  const filteredClients = useMemo(() => {
    if (!clientSearchTerm.trim()) return clients;
    return clients.filter(c =>
      c.clientName.toLowerCase().includes(clientSearchTerm.toLowerCase())
    );
  }, [clients, clientSearchTerm]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // -------- HEADER client-side filtering --------
  const filteredHeaderData = useMemo(() => {
    // headerFilters.jobType now holds the jobTypeID; translate to name for text-match
    const selectedJobTypeName = headerFilters.jobType
      ? jobTypeNameById[String(headerFilters.jobType)]
      : "";
    return listAnalysisHeaderData.filter((row) => {
      if (headerFilters.listName && !containsText(row.listName, headerFilters.listName)) return false;
      if (selectedJobTypeName && !containsText(row.jobType, selectedJobTypeName)) return false;
      if (headerFilters.search && !containsText(row.reh, headerFilters.search)) return false;
      if (!inDateRange(row.lastUsed, headerFilters.fromDate, headerFilters.toDate)) return false;
      return true;
    });
  }, [listAnalysisHeaderData, headerFilters, jobTypeNameById]);

  // -------- DETAIL client-side filtering --------
  const filteredDetailData = useMemo(() => {
    return listAnalysisDetailData.filter((row) => {
      if (detailFilters.reh && detailFilters.reh !== "All" && !containsText(row.reh, detailFilters.reh)) return false;
      if (detailFilters.mailCode && !containsText(row.mailCode, detailFilters.mailCode)) return false;
      if (detailFilters.listName && !containsText(row.listName, detailFilters.listName)) return false;
      if (!inDateRange(row.mailDate, detailFilters.fromDate, detailFilters.toDate)) return false;
      return true;
    });
  }, [listAnalysisDetailData, detailFilters]);


  const handleSelectAll = () => {
    setSelectedClients(clients);
    const ids = clients.map(c => c.clientID);
    setSelectedClientIds(ids);
    fetchJobsByClient?.(ids);

    // ✅ clear jobs (new client context)
    setLolFilters((p) => ({ ...p, jobIds: [] }));
    setDetailFilters((p) => ({ ...p, jobIds: [] }));
  };



  const handleClearAll = () => {
    setSelectedClients([]);
    setSelectedClientIds([]);
    fetchJobsByClient?.([]);

    // ✅ clear jobs as well
    setLolFilters((p) => ({ ...p, jobIds: [] }));
    setDetailFilters((p) => ({ ...p, jobIds: [] }));
  };


  return (
    <>

      <SuccessMessage
        message={message}
        onClose={handleMessageClose}
        type={messageType}
      />

      {/* Report type dropdown */}
      <div style={{ marginTop: "20px", width: "250px" }}>
        <label style={{ fontWeight: "600", display: "block", marginBottom: "8px" }}>
          Select Report Type
        </label>
        <select
          style={styles.input}
          value={listAnalysisType}
          onChange={handleReportTypeChange}
        >
          <option>List of Lists</option>
          <option>List Analysis - Header</option>
          <option>List Analysis - Detail</option>
          {/* <option>Trend Analysis</option> */}
        </select>
      </div>

      {/* ================= LIST OF LISTS ================= */}
      {listAnalysisType === "List of Lists" && (
        <>
          <div style={{ marginTop: "20px", marginBottom: "10px" }}>
          </div>

          <div className="report-filter-container">
            <div className="filter-header" style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '0px 10px 0px 0px', alignItems: 'center',
              backgroundColor: '#2E6F9F', borderRadius: '8px 8px 0px 0px'
            }}>
              <h2 className='filter-heading' style={{ margin: 0, color: '#fff' }}>Filters</h2>
              <button
                onClick={toggleFilters}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '12px', color: '#fff', padding: '2px 6px',
                  transition: 'transform 0.2s ease',
                  transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                aria-label={showFilters ? 'Hide filters' : 'Show filters'}
              >
                <img src="../assets/upward-arrow-icon.png"
                  alt={showFilters ? 'Hide filters' : 'Show filters'}
                  style={{ width: '12px', height: '12px', transition: 'transform 0.2s ease' }} />
              </button>
            </div>

            {showFilters && (
              <>
                <div className="report-filter-body">
                  <div className="report-filter-row">
                    <div className="report-filter-field" style={{ position: 'relative', width: '350px' }}>
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                        Client(s)
                      </label>

                      <div
                        ref={clientDropdownRef}
                        style={{ position: "relative", width: "100%" }}
                      >
                        {/* INPUT BOX */}
                        <div
                          onClick={() => setShowClientDropdown(true)}
                          style={{
                            minHeight: "38px",
                            height: "auto",
                            boxSizing: "border-box",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            paddingLeft: "8px",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                            cursor: "text",
                            backgroundColor: "#fff",
                            alignItems: "center",
                            overflowY: "auto"
                          }}
                        >
                          {/* SELECTED CHIPS */}
                          {selectedClients.length > 1 ? (
                            <span
                              style={{
                                backgroundColor: "#e9ecef",
                                padding: "2px 6px",
                                borderRadius: "3px",
                                fontSize: "13px",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {selectedClients.length} clients selected
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClearAll();
                                }}
                                style={{ marginLeft: 6, cursor: "pointer" }}
                              >
                                ×
                              </span>
                            </span>
                          ) : (
                            selectedClients.map((client) => (
                              <span
                                key={client.clientID}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  backgroundColor: "#e9ecef",
                                  padding: "2px 6px",
                                  borderRadius: "3px",
                                  fontSize: "13px",
                                  whiteSpace: "nowrap"
                                }}
                              >
                                {client.clientName}
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClientChangeMulti(client);
                                  }}
                                  style={{ marginLeft: 6, cursor: "pointer" }}
                                >
                                  ×
                                </span>
                              </span>
                            ))
                          )}

                          {/* SEARCH INPUT */}
                          <input
                            type="text"
                            placeholder={
                              selectedClients.length === 0 ? "Select clients..." : ""
                            }
                            value={clientSearchTerm}
                            onChange={(e) => {
                              setClientSearchTerm(e.target.value);
                              setShowClientDropdown(true);
                            }}
                            style={{
                              flex: 1,
                              minWidth: selectedClients.length === 0 ? "120px" : "40px",
                              width: "40px",
                              border: "none",
                              outline: "none",
                              fontSize: "14px",
                              padding: 0
                            }}
                          />
                        </div>

                        {/* DROPDOWN */}
                        {showClientDropdown && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              width: "100%",
                              backgroundColor: "#fff",
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                              marginTop: "4px",
                              zIndex: 9999,
                              maxHeight: "250px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                              overflow: "hidden"
                            }}
                          >
                            {/* ACTIONS */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                padding: "6px",
                                borderBottom: "1px solid #eee"
                              }}
                            >
                              <button style={{
                                background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F',
                                padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                              }} onClick={handleSelectAll}>Select All</button>
                              <button style={{
                                background: 'none', border: '1px solid #dc3545', color: '#dc3545',
                                padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                              }} onClick={handleClearAll}>Clear All</button>
                            </div>

                            {/* LIST */}
                            <div style={{ overflowY: "auto", maxHeight: "200px" }}>
                              {filteredClients.map((client) => (
                                <div
                                  key={client.clientID}
                                  onClick={() => handleClientChangeMulti(client)}
                                  style={{
                                    padding: "8px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    backgroundColor: selectedClients.some(
                                      (c) => c.clientID === client.clientID
                                    )
                                      ? "#e6f7ff"
                                      : "#fff"
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedClients.some(
                                      (c) => c.clientID === client.clientID
                                    )}
                                    readOnly
                                    style={{ marginRight: "6px", }}
                                  />
                                  <span style={{ fontSize: "12px" }}> {client.clientName} </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* JOB ID MULTI-SELECT */}
                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Job ID</label>


                      <JobIdMultiSelect
                        jobs={selectedClientIds.length ? jobDetailsJobId : []}
                        selectedIds={lolFilters.jobIds || []}
                        setSelectedIds={(ids) => setLolFilters((p) => ({ ...p, jobIds: ids }))}
                      />


                    </div>

                    {/* MAIL DATE */}
                    <div className="mail-date-filter">
                      <label className="mail-date-filter-label">
                        Mail Date
                      </label>
                      <div className="">
                        <div className="date-input-wrapper">
                          <input
                            type="date"
                            className={`mail-date-input ${!lolFilters.fromDate ? 'empty' : ''}`}
                            value={lolFilters.fromDate}
                            onChange={(e) => {
                              const newFromDate = e.target.value;
                              if (lolFilters.toDate && newFromDate > lolFilters.toDate) {
                                return;
                              }
                              setLolFilters((p) => ({ ...p, fromDate: newFromDate }));
                            }}
                            max={lolFilters.toDate || undefined}
                            title="Start Date"
                          />
                        </div>
                        <span className="arrow-icon">→</span>
                        <div className="date-input-wrapper">
                          <input
                            type="date"
                            className={`mail-date-input ${!lolFilters.toDate ? 'empty' : ''}`}
                            value={lolFilters.toDate}
                            onChange={(e) => {
                              const newToDate = e.target.value;
                              if (lolFilters.fromDate && newToDate < lolFilters.fromDate) {
                                return;
                              }
                              setLolFilters((p) => ({ ...p, toDate: newToDate }));
                            }}
                            min={lolFilters.fromDate || undefined}
                            title="End Date"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>List Name</label>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={lolFilters.listName}
                        onChange={(e) => setLolFilters((p) => ({ ...p, listName: e.target.value }))}
                      />
                    </div>

                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Mail Code</label>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={lolFilters.mailCode}
                        onChange={(e) => setLolFilters((p) => ({ ...p, mailCode: e.target.value }))}
                      />
                    </div>

                    {/* POSTAGE MULTI-SELECT (sent to API on Run) */}
                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Postage</label>
                      <PostageMultiSelect
                        postages={postages}
                        selectedIds={lolFilters.postageIds || []}
                        setSelectedIds={(ids) => setLolFilters((p) => ({ ...p, postageIds: ids }))}
                      />
                    </div>
                  </div>

                  <div className="report-filter-row" style={{ justifyContent: "flex-end" }}>
                    <div className="report-filter-buttons">
                      {(() => {
                        const hasFilters =
                          selectedClients.length > 0 ||
                          !!lolFilters.listName ||
                          !!lolFilters.fromDate ||
                          !!lolFilters.toDate ||
                          !!lolFilters.listId ||
                          (lolFilters.jobIds || []).length > 0 ||
                          !!lolFilters.mailCode ||
                          (lolFilters.postageIds || []).length > 0;
                        return (
                          <>
                            <button
                              className="report-btn-run"
                              onClick={() => fetchListOfLists(selectedClientIds)}
                              disabled={!hasFilters}
                              style={
                                !hasFilters
                                  ? { opacity: 0.5, cursor: "not-allowed" }
                                  : undefined
                              }
                            >
                              Run Report
                            </button>

                            <button
                              className="report-btn-clear"
                              onClick={() => {
                                // ✅ clear filters
                                setLolFilters({
                                  listName: "",
                                  fromDate: "",
                                  toDate: "",
                                  listId: "",
                                  jobIds: [],
                                  mailCode: "",
                                  postageIds: [],
                                });

                                // ✅ clear client dropdown
                                setSelectedClients([]);
                                setSelectedClientIds([]);
                                setClientSearchTerm("");

                                setListOfListsData([]);
                              }}
                              disabled={!hasFilters}
                              style={
                                !hasFilters
                                  ? { opacity: 0.5, cursor: "not-allowed" }
                                  : undefined
                              }
                            >
                              Clear
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
            <ExportMenu
              show={showExportMenu}
              setShow={setShowExportMenu}
              exportRef={exportRef}
              onExport={() =>
                exportToExcelCommon({
                  data: filteredLolData,
                  columnDefs: getListOfListsColumnDefs(navigate, lolFilters, selectedClientIds, filteredLolData),
                  sheetName: "List Of Lists",
                })
              }
              onPrintPDF={() =>
                printToPDFCommon({
                  data: filteredLolData,
                  columnDefs: getListOfListsColumnDefs(navigate, lolFilters, selectedClientIds, filteredLolData),
                  sheetName: "List Of Lists",
                  titleText: "List Of Lists Report",
                  subtitleText: "LIST OF LISTS",
                })
              }
            />
          </div>


          <div style={{ marginTop: "20px" }}>
            <AgGrid {...gridProps}
              rowData={filteredLolData}
              columnDefs={getListOfListsColumnDefs(navigate, lolFilters, selectedClientIds, filteredLolData)}
              gridOptions={{

                // ✅ Row-selection checkboxes — mirrors JobsModule/
                // JobReportsTab. The shared AgGrid wrapper supplies the
                // checkbox column automatically once rowSelection is set.
                suppressRowClickSelection: true,
                rowSelection: 'multiple',
                isRowSelectable: (params) => !params.data || !params.data.isTotalsRow,

                overlayNoRowsTemplate: `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      text-align: center;
    ">

      <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">
        📋
      </div>

      <div style="
        font-size: 16px;
        color: #6c757d;
        font-weight: 500;
      ">
        No records found
      </div>

    </div>
      `,

              }}
              frameworkComponents={{
                customTooltip: CustomTooltip
              }}
            />
          </div>
        </>
      )}

      {/* ================= HEADER ================= */}
      {listAnalysisType === "List Analysis - Header" && (
        <>
          <div className="report-filter-container">

            <div className="filter-header" style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '0px 10px 0px 0px', alignItems: 'center',
              backgroundColor: '#2E6F9F', borderRadius: '8px 8px 0px 0px'
            }}>
              <h2 className='filter-heading' style={{ margin: 0, color: '#fff' }}>Filters</h2>
              <button
                onClick={toggleFilters}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '12px', color: '#fff', padding: '2px 6px',
                  transition: 'transform 0.2s ease',
                  transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                aria-label={showFilters ? 'Hide filters' : 'Show filters'}
              >
                <img src="../assets/upward-arrow-icon.png"
                  alt={showFilters ? 'Hide filters' : 'Show filters'}
                  style={{ width: '12px', height: '12px', transition: 'transform 0.2s ease' }} />
              </button>
            </div>


            {showFilters && (
              <>
                <div className="report-filter-body">

                  <div className="report-filter-row">

                    <div className="report-filter-field" style={{ position: 'relative', width: '350px' }}>
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                        Client(s)
                      </label>

                      <div ref={clientDropdownRef} style={{ position: "relative", width: "100%" }}>

                        {/* INPUT BOX */}
                        <div
                          onClick={() => setShowClientDropdown(true)}
                          style={{
                            minHeight: "38px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            paddingLeft: "8px",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                            cursor: "text",
                            backgroundColor: "#fff",
                            alignItems: "center",
                            overflowY: "auto"
                          }}
                        >
                          {/* CHIPS */}
                          {selectedClients.length > 1 ? (
                            <span style={{
                              background: "#e9ecef",
                              padding: "2px 6px",
                              borderRadius: "3px",
                              fontSize: "13px"
                            }}>
                              {selectedClients.length} clients selected
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClearAll();
                                }}
                                style={{ marginLeft: 6, cursor: "pointer" }}
                              >
                                ×
                              </span>
                            </span>
                          ) : (
                            selectedClients.map((c) => (
                              <span key={c.clientID} style={{
                                display: "inline-flex",
                                background: "#e9ecef",
                                padding: "2px 6px",
                                borderRadius: "3px"
                              }}>
                                {c.clientName}
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClientChangeMulti(c);
                                  }}
                                  style={{ marginLeft: 6, cursor: "pointer" }}
                                >
                                  ×
                                </span>
                              </span>
                            ))
                          )}



                          {/* SEARCH */}
                          <input
                            type="text"
                            placeholder={selectedClients.length === 0 ? "Select clients..." : ""}
                            value={clientSearchTerm}
                            onChange={(e) => {
                              setClientSearchTerm(e.target.value);
                              setShowClientDropdown(true);
                            }}
                            style={{
                              flex: 1,
                              minWidth: selectedClients.length === 0 ? "120px" : "40px",
                              border: "none",
                              outline: "none",
                              fontSize: "14px"
                            }}
                          />
                        </div>

                        {/* DROPDOWN */}
                        {showClientDropdown && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              width: "100%",
                              backgroundColor: "#fff",
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                              marginTop: "4px",
                              zIndex: 9999,
                              maxHeight: "250px"
                            }}
                          >
                            {/* ACTIONS */}
                            <div style={{
                              display: "flex",
                              justifyContent: "space-between",
                              padding: "6px",
                              borderBottom: "1px solid #eee"
                            }}>
                              <button style={{ background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} onClick={handleSelectAll}>Select All</button>
                              <button style={{ background: 'none', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} onClick={handleClearAll}>Clear All</button>
                            </div>

                            {/* LIST */}
                            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                              {filteredClients.map((client) => (
                                <div
                                  key={client.clientID}
                                  onClick={() => handleClientChangeMulti(client)}
                                  style={{
                                    padding: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    backgroundColor: selectedClients.some(
                                      (c) => c.clientID === client.clientID
                                    )
                                      ? "#e6f7ff"
                                      : "#fff"
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedClients.some(
                                      (c) => c.clientID === client.clientID
                                    )}
                                    readOnly
                                    style={{ marginRight: "6px" }}
                                  />
                                  <span style={{ fontSize: "12px" }}>
                                    {client.clientName}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* JOB ID MULTI-SELECT ✅ ADD THIS */}
                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Job ID</label>
                      <JobIdMultiSelect
                        jobs={selectedClientIds.length ? jobDetailsJobId : []}
                        selectedIds={headerFilters.jobIds || []}
                        setSelectedIds={(ids) => setHeaderFilters((p) => ({ ...p, jobIds: ids }))}
                      />
                    </div>

                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>List Name</label>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={headerFilters.listName}
                        onChange={(e) => setHeaderFilters((p) => ({ ...p, listName: e.target.value }))}
                      />
                    </div>
                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>List Manager</label>
                      <select
                        value={headerFilters.manager}
                        onChange={(e) => setHeaderFilters((p) => ({ ...p, manager: e.target.value }))}
                      >
                        <option value="">All Managers</option>
                        {listManagers.map((m) => (
                          <option key={m.listManagerID} value={m.listManagerID}>
                            {m.listManagerName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* DATE RANGE */}
                    <div className="mail-date-filter">
                      <label className="mail-date-filter-label">
                        Date Range
                      </label>
                      <div className="">
                        <div className="date-input-wrapper">
                          <input
                            type="date"
                            className={`mail-date-input ${!headerFilters.fromDate ? 'empty' : ''}`}
                            value={headerFilters.fromDate}
                            onChange={(e) => {
                              const newFromDate = e.target.value;
                              if (headerFilters.toDate && newFromDate > headerFilters.toDate) {
                                return;
                              }
                              setHeaderFilters((p) => ({ ...p, fromDate: newFromDate }));
                            }}
                            max={headerFilters.toDate || undefined}
                            title="Start Date"
                          />
                        </div>
                        <span className="arrow-icon">→</span>
                        <div className="date-input-wrapper">
                          <input
                            type="date"
                            className={`mail-date-input ${!headerFilters.toDate ? 'empty' : ''}`}
                            value={headerFilters.toDate}
                            onChange={(e) => {
                              const newToDate = e.target.value;
                              if (headerFilters.fromDate && newToDate < headerFilters.fromDate) {
                                return;
                              }
                              setHeaderFilters((p) => ({ ...p, toDate: newToDate }));
                            }}
                            min={headerFilters.fromDate || undefined}
                            title="End Date"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Job Type</label>
                      <select
                        value={headerFilters.jobType}
                        onChange={(e) => setHeaderFilters((p) => ({ ...p, jobType: e.target.value }))}
                      >
                        <option value="">Select Job Type</option>
                        {jobTypes?.map((jobType) => (
                          // ✅ value is the ID so JobTypeID is sent to the API
                          <option key={jobType.jobTypeID} value={jobType.jobTypeID}>
                            {jobType.jobTypeName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Search</label>
                      <input
                        type="text"
                        placeholder="Rental/Exchange/House"
                        value={headerFilters.search}
                        onChange={(e) => setHeaderFilters((p) => ({ ...p, search: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="report-filter-row" style={{ justifyContent: "flex-end" }}>

                    <div className="report-filter-buttons">
                      {(() => {
                        const hasFilters =
                          selectedClients.length > 0 ||
                          !!headerFilters.listName ||
                          !!headerFilters.manager ||
                          !!headerFilters.fromDate ||
                          !!headerFilters.toDate ||
                          !!headerFilters.jobType ||
                          (headerFilters.jobIds || []).length > 0 ||  // ✅ ADD THIS LINE
                          !!headerFilters.search;

                        return (
                          <>
                            <button
                              className="report-btn-run"
                              // ✅ pass the args explicitly (was passing the click event)
                              onClick={() =>
                                fetchListAnalysisHeader({
                                  clientIds: selectedClientIds,
                                  jobTypeId: headerFilters.jobType, // ID, not name
                                })
                              }
                              disabled={!hasFilters}
                              style={!hasFilters ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                            >
                              Run Report
                            </button>

                            <button
                              className="report-btn-clear"
                              onClick={() => {
                                setHeaderFilters({
                                  listName: "",
                                  manager: "",
                                  fromDate: "",
                                  toDate: "",
                                  jobType: "",
                                  jobIds: [],
                                  search: "",
                                });

                                // ✅ same client reset as List of Lists
                                setSelectedClients([]);
                                setSelectedClientIds([]);
                                setClientSearchTerm("");

                                setShowClientDropdown(false);

                                setSearchText("");
                                setListAnalysisHeaderData([]);
                              }}
                              disabled={!hasFilters}
                              style={!hasFilters ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                            >
                              Clear
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

              </>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
            <ExportMenu
              show={showExportMenu}
              setShow={setShowExportMenu}
              exportRef={exportRef}
              onExport={() =>
                exportToExcelCommon({
                  data: filteredHeaderData,
                  columnDefs: filterColumnsForRole(getListAnalysisHeaderColumnDefs(), headerHiddenFields),
                  sheetName: "List Analysis Header",
                })
              }
              onPrintPDF={() =>
                printToPDFCommon({
                  data: filteredHeaderData,
                  columnDefs: filterColumnsForRole(getListAnalysisHeaderColumnDefs(), headerHiddenFields),
                  sheetName: "List Analysis Header",
                  titleText: "List Analysis - Header Report",
                  subtitleText: "LIST ANALYSIS · HEADER",
                })
              }
            />
          </div>


          <div style={{ marginTop: "20px" }}>
            <AgGrid
              {...gridProps}
              rowData={filteredHeaderData}
              columnDefs={filterColumnsForRole(getListAnalysisHeaderColumnDefs(), headerHiddenFields)}
              gridOptions={{

                // ✅ Row-selection checkboxes — mirrors JobsModule/
                // JobReportsTab. The shared AgGrid wrapper supplies the
                // checkbox column automatically once rowSelection is set.
                suppressRowClickSelection: true,
                rowSelection: 'multiple',
                isRowSelectable: (params) => !params.data || !params.data.isTotalsRow,

                overlayNoRowsTemplate: `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      text-align: center;
    ">

      <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">
        📋
      </div>

      <div style="
        font-size: 16px;
        color: #6c757d;
        font-weight: 500;
      ">
        No records found
      </div>

    </div>
      `,

              }}
              frameworkComponents={{
                customTooltip: CustomTooltip
              }}
            />
          </div>
        </>
      )}

      {/* ================= DETAIL ================= */}
      {listAnalysisType === "List Analysis - Detail" && (
        <>
          <div style={{ marginTop: "20px", marginBottom: "10px" }}>
          </div>

          <div className="report-filter-container">
            <div className="filter-header" style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '0px 10px 0px 0px', alignItems: 'center',
              backgroundColor: '#2E6F9F', borderRadius: '8px 8px 0px 0px'
            }}>
              <h2 className='filter-heading' style={{ margin: 0, color: '#fff' }}>Filters</h2>
              <button
                onClick={toggleFilters}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '12px', color: '#fff', padding: '2px 6px',
                  transition: 'transform 0.2s ease',
                  transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                aria-label={showFilters ? 'Hide filters' : 'Show filters'}
              >
                <img src="../assets/upward-arrow-icon.png"
                  alt={showFilters ? 'Hide filters' : 'Show filters'}
                  style={{ width: '12px', height: '12px', transition: 'transform 0.2s ease' }} />
              </button>
            </div>
            {showFilters && (
              <>
                <div className="report-filter-body">
                  <div className="report-filter-row">
                    <div className="report-filter-field" style={{ position: 'relative', width: '350px' }}>
                      <label style={{ color: '#000', fontSize: '16px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                        Client(s)
                      </label>

                      <div ref={clientDropdownRef} style={{ position: "relative", width: "100%" }}>

                        {/* INPUT BOX */}
                        <div
                          onClick={() => setShowClientDropdown(true)}
                          style={{
                            minHeight: "38px",
                            height: "auto",
                            boxSizing: "border-box",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            paddingLeft: "8px",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                            cursor: "text",
                            backgroundColor: "#fff",
                            alignItems: "center",
                            overflowY: "auto"
                          }}
                        >
                          {/* CHIPS */}
                          {selectedClients.length > 1 ? (
                            <span style={{
                              background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", fontSize: "13px",
                              whiteSpace: "nowrap"
                            }}>
                              {selectedClients.length} clients selected
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClearAll();
                                }}
                                style={{ marginLeft: 6, cursor: "pointer" }}
                              >
                                ×
                              </span>
                            </span>
                          ) : (
                            selectedClients.map((c) => (
                              <span key={c.clientID} style={{
                                display: "inline-flex", background: "#e9ecef", padding: "2px 6px", fontSize: "13px",
                                whiteSpace: "nowrap", borderRadius: "3px"
                              }}>
                                {c.clientName}
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClientChangeMulti(c);
                                  }}
                                  style={{ marginLeft: 6, cursor: "pointer" }}
                                >
                                  ×
                                </span>
                              </span>
                            ))
                          )}

                          {/* SEARCH */}
                          <input
                            type="text"
                            placeholder={selectedClients.length === 0 ? "Select clients..." : ""}
                            value={clientSearchTerm}
                            onChange={(e) => {
                              setClientSearchTerm(e.target.value);
                              setShowClientDropdown(true);
                            }}
                            style={{
                              flex: 1,
                              minWidth: selectedClients.length === 0 ? "120px" : "40px",
                              width: "40px",
                              border: "none",
                              outline: "none",
                              fontSize: "14px",
                              padding: 0
                            }}
                          />
                        </div>

                        {/* DROPDOWN */}
                        {showClientDropdown && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              width: "100%",
                              backgroundColor: "#fff",
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                              marginTop: "4px",
                              zIndex: 9999,
                              maxHeight: "250px",
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
                              <button onClick={handleSelectAll} style={{
                                background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F',
                                padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                              }} >Select All</button>
                              <button onClick={handleClearAll} style={{
                                background: 'none', border: '1px solid #dc3545', color: '#dc3545',
                                padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                              }}>Clear All</button>
                            </div>

                            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                              {filteredClients.map((client) => (
                                <div
                                  key={client.clientID}
                                  onClick={() => handleClientChangeMulti(client)}
                                  style={{
                                    padding: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    background: selectedClients.some(c => c.clientID === client.clientID)
                                      ? "#e6f7ff"
                                      : "#fff"
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedClients.some(c => c.clientID === client.clientID)}
                                    readOnly
                                    style={{ marginRight: "6px" }}
                                  />
                                  <span style={{ fontSize: "12px" }}> {client.clientName} </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* JOB ID MULTI-SELECT */}
                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Job ID</label>

                      <JobIdMultiSelect
                        jobs={selectedClientIds.length ? jobDetailsJobId : []}
                        selectedIds={lolFilters.jobIds || []}
                        setSelectedIds={(ids) => setLolFilters((p) => ({ ...p, jobIds: ids }))}
                      />

                    </div>

                    {/* MAIL DATE */}
                    <div className="mail-date-filter">
                      <label className="mail-date-filter-label">
                        Mail Date
                      </label>
                      <div className="">
                        <div className="date-input-wrapper">
                          <input
                            type="date"
                            className={`mail-date-input ${!detailFilters.fromDate ? 'empty' : ''}`}
                            value={detailFilters.fromDate}
                            onChange={(e) => {
                              const newFromDate = e.target.value;
                              if (detailFilters.toDate && newFromDate > detailFilters.toDate) {
                                return;
                              }
                              setDetailFilters((p) => ({ ...p, fromDate: newFromDate }));
                            }}
                            max={detailFilters.toDate || undefined}
                            title="Start Date"
                          />
                        </div>
                        <span className="arrow-icon">→</span>
                        <div className="date-input-wrapper">
                          <input
                            type="date"
                            className={`mail-date-input ${!detailFilters.toDate ? 'empty' : ''}`}
                            value={detailFilters.toDate}
                            onChange={(e) => {
                              const newToDate = e.target.value;
                              if (detailFilters.fromDate && newToDate < detailFilters.fromDate) {
                                return;
                              }
                              setDetailFilters((p) => ({ ...p, toDate: newToDate }));
                            }}
                            min={detailFilters.fromDate || undefined}
                            title="End Date"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Rental/Exchange/House</label>
                      <select
                        value={detailFilters.reh}
                        onChange={(e) => setDetailFilters((p) => ({ ...p, reh: e.target.value }))}
                      >
                        <option value="">All</option>
                        <option>Rental</option>
                        <option>Exchange</option>
                        <option>House</option>
                      </select>
                    </div>

                    {/* JOB TYPE — value is the ID (sent as JobTypeID to API) */}
                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Job Type</label>
                      <select
                        value={detailFilters.jobType}
                        onChange={(e) => setDetailFilters((p) => ({ ...p, jobType: e.target.value }))}
                      >
                        <option value="">Select Job Type</option>
                        {jobTypes?.map((jobType) => (
                          <option key={jobType.jobTypeID} value={jobType.jobTypeID}>
                            {jobType.jobTypeName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Mail Code</label>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={detailFilters.mailCode}
                        onChange={(e) => setDetailFilters((p) => ({ ...p, mailCode: e.target.value }))}
                      />
                    </div>
                    <div className="report-filter-field">
                      <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>List Name</label>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={detailFilters.listName}
                        onChange={(e) => setDetailFilters((p) => ({ ...p, listName: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="report-filter-row" style={{ justifyContent: "flex-end" }}>

                    <div className="report-filter-buttons" >
                      {(() => {

                        const hasFilters =
                          selectedClientIds.length > 0 ||
                          (detailFilters.jobIds || []).length > 0 ||
                          !!detailFilters.fromDate ||
                          !!detailFilters.toDate ||
                          !!detailFilters.reh ||
                          !!detailFilters.jobType ||
                          !!detailFilters.mailCode ||
                          !!detailFilters.listName;


                        return (
                          <>
                            <button
                              className="report-btn-run"
                              onClick={() =>
                                fetchListAnalysisDetail({
                                  clientIds: selectedClientIds,
                                  jobIds: detailFilters.jobIds || [],
                                  jobType: detailFilters.jobType,
                                  reh: detailFilters.reh,
                                  mailCode: detailFilters.mailCode,
                                  listName: detailFilters.listName,
                                  startDate: detailFilters.fromDate,
                                  endDate: detailFilters.toDate,
                                })
                              }
                              disabled={!hasFilters}
                              style={!hasFilters ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                            >
                              Run Report
                            </button>
                            <button
                              className="report-btn-clear"
                              onClick={() => {
                                setDetailFilters({
                                  clientId: "",
                                  jobIds: [],
                                  fromDate: "",
                                  toDate: "",
                                  reh: "",
                                  jobType: "",
                                  mailCode: "",
                                  listName: "",
                                });

                                setSelectedClients([]);
                                setSelectedClientIds([]);
                                setClientSearchTerm("");

                                setSearchText("");

                                setListAnalysisDetailData([]);

                              }}
                              disabled={!hasFilters}
                              style={
                                !hasFilters
                                  ? { opacity: 0.5, cursor: "not-allowed" }
                                  : undefined
                              }
                            >
                              Clear
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
            <ExportMenu
              show={showExportMenu}
              setShow={setShowExportMenu}
              exportRef={exportRef}
              onExport={() =>
                exportToExcelCommon({
                  data: filteredDetailData,
                  columnDefs: filterColumnsForRole(
                    getListAnalysisDetailColumnDefs(navigate, detailFilters, selectedClientIds, filteredDetailData),
                    detailHiddenFields
                  ),
                  sheetName: "List Analysis Detail",
                })
              }
              onPrintPDF={() =>
                printToPDFCommon({
                  data: filteredDetailData,
                  columnDefs: filterColumnsForRole(
                    getListAnalysisDetailColumnDefs(navigate, detailFilters, selectedClientIds, filteredDetailData),
                    detailHiddenFields
                  ),
                  sheetName: "List Analysis Detail",
                  titleText: "List Analysis - Detail Report",
                  subtitleText: "LIST ANALYSIS · DETAIL",
                })
              }
            />
          </div>

          <div style={{ marginTop: "20px" }}>
            <AgGrid
              {...gridProps}
              rowData={filteredDetailData}
              columnDefs={filterColumnsForRole(
                getListAnalysisDetailColumnDefs(navigate, detailFilters, selectedClientIds, filteredDetailData),
                detailHiddenFields
              )}
              gridOptions={{

                // ✅ Row-selection checkboxes — mirrors JobsModule/
                // JobReportsTab. The shared AgGrid wrapper supplies the
                // checkbox column automatically once rowSelection is set.
                suppressRowClickSelection: true,
                rowSelection: 'multiple',
                isRowSelectable: (params) => !params.data || !params.data.isTotalsRow,

                overlayNoRowsTemplate: `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      text-align: center;
    ">

      <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">
        📋
      </div>

      <div style="
        font-size: 16px;
        color: #6c757d;
        font-weight: 500;
      ">
        No records found
      </div>

    </div>
      `,

              }}
              frameworkComponents={{
                customTooltip: CustomTooltip
              }}
            />
          </div>
        </>
      )}


    </>
  );
};

export default ListAnalysisTab;